import React from 'react';
import { Alert, Box, Breadcrumbs, Button, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

import FPlanningJourney from '../core/FPlanningJourney';

export type FWorkbenchBreadcrumb = { label: string; onClick?: () => void };

export type FWorkbenchShellProps = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  headerExtension?: React.ReactNode;
  breadcrumbs?: FWorkbenchBreadcrumb[];
  processStage?: React.ReactNode;
  summary?: React.ReactNode;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  saving?: boolean;
  dirty?: boolean;
  error?: string;
  success?: string;
  warning?: string;
  info?: string;
  phases?: any[];
  phasecode?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  onHelp?: () => void;
  children: React.ReactNode;
};

class FWorkbenchShell extends React.PureComponent<FWorkbenchShellProps> {
  renderBreadcrumbs() {
    const b = this.props.breadcrumbs || [];
    if (!b.length) return null;

    return (
      <Breadcrumbs sx={{ mb: 1 }}>
        {b.map((x, i) => (
          <Typography
            key={`${x.label}-${i}`}
            variant="caption"
            color={x.onClick ? 'primary' : 'text.secondary'}
            sx={{ cursor: x.onClick ? 'pointer' : 'default' }}
            onClick={x.onClick}
          >
            {x.label}
          </Typography>
        ))}
      </Breadcrumbs>
    );
  }

  renderStatus() {
    const { loading, saving, dirty } = this.props;

    if (saving) {
      return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="caption">Saving…</Typography></Stack>;
    }

    if (loading) {
      return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="caption">Loading…</Typography></Stack>;
    }

    if (dirty) return <Typography variant="caption" color="warning.main">● Unsaved changes</Typography>;

    return null;
  }

  renderAlerts() {
    const { error, success, warning, info } = this.props;
    return (
      <>
        {error && <Alert severity="error">{error}</Alert>}
        {warning && <Alert severity="warning">{warning}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {info && <Alert severity="info">{info}</Alert>}
      </>
    );
  }

  render() {
    const p = this.props;

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Paper elevation={1} sx={{ p: 2 }}>
            {this.renderBreadcrumbs()}

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {this.props.onBack && (
                  <Tooltip title="Back" arrow>
                    <IconButton
                      disabled={this.props.loading}
                      onClick={this.props.onBack}
                    >
                      <ArrowBackOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {p.icon && <Box sx={{ display: 'flex', color: 'primary.main' }}>{p.icon}</Box>}
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{p.title}</Typography>
                  {p.subtitle && <Typography variant="body2" color="text.secondary">{p.subtitle}</Typography>}
                </Box>
              </Stack>

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                  {this.renderStatus()}
                  {p.onRefresh && <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={p.onRefresh}>Refresh</Button>}
                  {p.onHelp && <Button size="small" variant="outlined" startIcon={<HelpOutlineIcon />} onClick={p.onHelp}>Help</Button>}
                  {p.actions}
                </Stack>
                {/* <FPlanningJourney
                  //phases={this.state.planningCyclePhases} 
                  //currentPhaseCode={this.state.activePlanningCycle?.phasecode}
                  phases={p.phases}
                  currentPhaseCode={p.phasecode}
                /> */}
                {this.props.headerExtension && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mt: 0.5,
                    }}
                  >
                    {this.props.headerExtension}
                  </Box>
                )}
              </Stack>
            </Stack>
          </Paper>

          {p.processStage && <Box>{p.processStage}</Box>}
          {p.summary && <Box>{p.summary}</Box>}
          {p.filters && <Box>{p.filters}</Box>}
          {this.renderAlerts()}
          <Box>{p.children}</Box>
        </Stack>
      </Box>
    );
  }
}

export default FWorkbenchShell;
